import { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Module-level cache so every form mount doesn't refetch.
let _cache = null;
let _inflight = null;
const _listeners = new Set();

async function _fetch() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = axios
    .get(`${API}/field-form-options`)
    .then((res) => {
      _cache = res.data || {};
      _inflight = null;
      _listeners.forEach((cb) => {
        try { cb(_cache); } catch {}
      });
      return _cache;
    })
    .catch((err) => {
      _inflight = null;
      // Don't poison cache on transient failure — fall back to defaults.
      console.warn('Field form options fetch failed; falling back to defaults', err);
      return {};
    });
  return _inflight;
}

/** Invalidate the cache after admin saves a new option list. */
export function invalidateFieldFormOptions() {
  _cache = null;
}

/**
 * Hook that returns the current option list for one field_key. While the
 * fetch is pending OR if the backend is unreachable, returns the caller's
 * supplied `fallback` array so the form is always usable.
 *
 * The hook ALSO merges any `legacyValues` (currently-selected values that
 * may no longer exist in the saved option list) into the returned options
 * so the user can still see and uncheck them when editing an old report.
 *
 * @param {string} fieldKey      e.g. 'locate.marked_color'
 * @param {Array}  fallback      [{value,color?}, ...] — the old hardcoded list
 * @param {Array}  legacyValues  optional list of selected string values to
 *                               keep visible even if removed from settings
 */
export function useFieldFormOptions(fieldKey, fallback = [], legacyValues = []) {
  // Normalise fallback so callers can pass either strings or {value} objects.
  const fbList = fallback.map((o) =>
    typeof o === 'string' ? { value: o } : o
  );
  const [options, setOptions] = useState(() => {
    if (_cache && _cache[fieldKey]) return _cache[fieldKey].options || fbList;
    return fbList;
  });

  useEffect(() => {
    let cancelled = false;
    _fetch().then((data) => {
      if (cancelled) return;
      if (data && data[fieldKey] && Array.isArray(data[fieldKey].options)) {
        setOptions(data[fieldKey].options);
      }
    });
    // Re-fetch when invalidated elsewhere.
    const listener = (data) => {
      if (data && data[fieldKey] && Array.isArray(data[fieldKey].options)) {
        setOptions(data[fieldKey].options);
      }
    };
    _listeners.add(listener);
    return () => {
      cancelled = true;
      _listeners.delete(listener);
    };
  }, [fieldKey]);

  // Merge legacy values that aren't in the current list. Tag them so the UI
  // can render a subtle "removed" hint next to them. Coerce legacyValues to
  // an array first — historical reports stored single-value fields as raw
  // strings (e.g. utility_type="Gas"), and calling .filter on a string
  // would crash the form viewer with a white screen.
  const legacyList = Array.isArray(legacyValues)
    ? legacyValues
    : legacyValues
      ? [legacyValues]
      : [];
  const known = new Set(options.map((o) => String(o.value)));
  const seen = new Set();
  const legacyExtra = [];
  for (const v of legacyList) {
    if (!v) continue;
    const key = String(v);
    if (known.has(key) || seen.has(key)) continue;
    seen.add(key);
    legacyExtra.push({ value: key, legacy: true });
  }
  return [...options, ...legacyExtra];
}
